/**
 * Real backend provider for invoice generation (issue #287), replacing the mock.
 * Implements the SAME `InvoiceGenerationProvider` interface the components already
 * depend on — no component changes needed to swap providers (see provider.ts).
 *
 * Known gaps (flagged, not silently dropped):
 * - `listUploadedInvoices()` invoices only ever have ONE synthetic line item —
 *   the older upload/extraction pipeline captures just a gross total, not
 *   structured lines (FIN-280 follow-up). `customerId` is pre-filled from a
 *   confident vendor-to-customer match when one exists (FIN-280) — still null,
 *   for the user to pick via CustomerPicker, when nothing matched confidently.
 * - `recurrence` is UI-only — no backend column yet (recurring processing is
 *   out of scope for this slice).
 */
import { invoiceGenerationApiClient, type InvoiceGenerationApiError } from "@/clients/invoiceGenerationApiClient";
import { supabase } from "@/integrations/supabase/client";
import { countryNameFor, DraftEditError, linesToSave } from "@financialharmony/invoicing";
import type {
  Article,
  Customer,
  CustomerType,
  IssueStatus,
  ReceivableDraft,
  Receivable,
  LineItem,
  NewCustomerInput,
  Seller,
  UploadedInvoice,
  ManualBankAccountInput,
  PaymentMethod,
  PaymentReferenceLine,
  ManualBankAccount,
  InvoiceGenerationProvider,
  InvoiceBankAccount,
  BankgiroReplacementNotice,
  DocumentLanguage,
  InvoiceCurrency,
} from "@financialharmony/invoicing";

// Backend rows are snake_case DB shapes; frontend domain types are camelCase.
// These interfaces describe just the fields this provider reads off each row —
// not full re-declarations of the generated backend types.
interface CustomerRowLike {
  id: string; name: string; entity_type: string; org_number: string | null;
  vat_number: string | null; country_code: string; email: string | null;
  address_street: string | null; address_postal_code: string | null;
  address_city: string | null; address_country: string | null;
}
interface ArticleRowLike {
  id: string; name: string; default_unit: string; default_unit_price: number;
  default_vat_rate: number; vat_category: string | null; times_used: number;
}
interface LineItemRowLike {
  id: string; article_id: string | null; description: string; quantity: number;
  unit: string; unit_price: number; vat_rate: number;
  vat_rate_manual?: boolean | null; vat_rate_automatic?: number | null; vat_category?: string | null;
}
interface ReceivableRowLike {
  id: string; customer_id: string | null; invoice_date: string; invoice_number: string | null;
  issue_status: string; payment_reference: string | null; gross_total: number | null;
  updated_at: string; pdf_path: string | null; draft_version?: number;
  /** FIN-1187: a re-rendered copy of a sent invoice; downloads prefer it, the original stays in pdf_path. */
  reissued_pdf_path?: string | null;
  /** The rendered draft PDF (FIN-829); null for a sent invoice and for a not-yet-rendered draft. */
  draftPdfPath?: string | null;
  lineItems: LineItemRowLike[];
  payment_terms_days: number | null; their_reference: string | null; our_reference: string | null;
  interest_rate_percent: number | null; free_text: string | null; payment_method: string | null;
  receiving_bank_account_id?: string | null;
  /** FIN-1580: optional for a pre-feature row read before the backend rolled this out. */
  document_language?: string | null;
  currency?: string | null;
}
interface ExtractedLineItemRowLike {
  id: string; financial_document_id: string; description: string;
  quantity: number; unit_price: number; sort_order: number;
}
interface UploadedInvoiceRowLike {
  id: string; vendor: string | null; amount: number | null; currency: string | null;
  remittance_information: string | null; customer_id: string | null;
  // FIN-280 — absent (older document) or empty (extraction found no reliable
  // breakdown) both take the pre-existing synthetic-line fallback below.
  extractedLineItems?: ExtractedLineItemRowLike[];
}
interface ManualBankAccountRowLike {
  id: string; clearingNumber: string; accountNumber: string; iban: string | null; bankName: string | null;
}
interface SellerLike {
  name: string; orgNumber: string; vatNumber: string | null; vatRegistered: boolean;
  fSkatt: boolean; address: string; addressStreet: string; addressPostalCode: string; addressCity: string;
  paymentReferenceLines: PaymentReferenceLine[]; manualBankAccounts: ManualBankAccountRowLike[]; logoUrl: string | null;
  defaultInterestRatePercent: number;
  resolvedPaymentMethod: PaymentMethod; bankgiroAvailable: boolean;
  invoiceBankAccounts?: InvoiceBankAccount[]; bankgiroReplacementNotice?: BankgiroReplacementNotice | null;
  receivingAccountChoiceRequired?: boolean;
  companyBankgiro?: string | null;
}

function mapManualBankAccount(row: ManualBankAccountRowLike): ManualBankAccount {
  return { id: row.id, clearingNumber: row.clearingNumber, accountNumber: row.accountNumber, iban: row.iban, bankName: row.bankName };
}

function mapSeller(s: SellerLike): Seller {
  return {
    name: s.name,
    orgNumber: s.orgNumber,
    vatNumber: s.vatNumber ?? "",
    vatRegistered: s.vatRegistered,
    fSkatt: s.fSkatt,
    address: s.address,
    addressStreet: s.addressStreet,
    addressPostalCode: s.addressPostalCode,
    addressCity: s.addressCity,
    paymentReferenceLines: s.paymentReferenceLines ?? [],
    manualBankAccounts: (s.manualBankAccounts ?? []).map(mapManualBankAccount),
    logoUrl: s.logoUrl,
    defaultInterestRatePercent: s.defaultInterestRatePercent,
    resolvedPaymentMethod: s.resolvedPaymentMethod,
    bankgiroAvailable: s.bankgiroAvailable,
    invoiceBankAccounts: s.invoiceBankAccounts ?? [],
    bankgiroReplacementNotice: s.bankgiroReplacementNotice ?? null,
    receivingAccountChoiceRequired: s.receivingAccountChoiceRequired ?? false,
    companyBankgiro: s.companyBankgiro ?? null,
  };
}

function mapCustomer(row: CustomerRowLike): Customer {
  return {
    id: row.id,
    name: row.name,
    type: row.entity_type as CustomerType,
    orgNumber: row.org_number ?? undefined,
    vatNumber: row.vat_number ?? undefined,
    country: row.country_code,
    countryName: countryNameFor(row.country_code),
    email: row.email ?? undefined,
    address: {
      street: row.address_street ?? "",
      postalCode: row.address_postal_code ?? "",
      city: row.address_city ?? "",
      country: row.address_country ?? countryNameFor(row.country_code),
    },
  };
}

function mapArticle(row: ArticleRowLike): Article {
  return {
    id: row.id,
    name: row.name,
    defaultUnit: row.default_unit,
    defaultUnitPrice: row.default_unit_price,
    defaultVatRate: row.default_vat_rate,
    vatCategory: row.vat_category ?? undefined,
    timesUsed: row.times_used,
  };
}

// customerId comes from the backend's own vendor-to-customer match (FIN-280,
// InvoiceGenerationService.listUploadedReceivables) — null when nothing matched
// confidently, never guessed here.
//
// FIN-280 F1/F3/EC3: real extracted lines (financial_document_line_items) pre-fill
// when extraction found a reliable breakdown; the pre-existing single synthetic
// line (gross total only) is the fallback for an older document or one where
// extraction found nothing reliable — both take the identical fallback path.
// `vatRate: 25` is a placeholder on EVERY line either way (Q2) — CreateInvoice's
// classify-on-mount effect overwrites it per line immediately; VAT is never read
// from the document itself (AC2).
function mapUploadedInvoice(row: UploadedInvoiceRowLike): UploadedInvoice {
  const amount = row.amount ?? 0;
  const currency = row.currency ?? "SEK";
  const extracted = row.extractedLineItems ?? [];
  const lines = extracted.length > 0
    ? [...extracted]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((l) => ({
          description: l.description,
          quantity: l.quantity,
          unit: "st",
          unitPrice: l.unit_price,
          vatRate: 25,
        }))
    : [{
        description: row.remittance_information
          ? `Invoice ${row.remittance_information}`
          : "Invoice (source document has no line breakdown)",
        quantity: 1,
        unit: "st",
        unitPrice: amount,
        vatRate: 25,
      }];
  return {
    id: row.id,
    vendor: row.vendor ?? null,
    amount,
    currency,
    customerId: row.customer_id,
    lines,
  };
}

function mapLineItem(row: LineItemRowLike): LineItem {
  return {
    id: row.id,
    articleId: row.article_id ?? undefined,
    description: row.description,
    quantity: row.quantity,
    unit: row.unit,
    unitPrice: row.unit_price,
    vatRate: row.vat_rate,
    vatManual: row.vat_rate_manual ?? false,
    // Left undefined rather than coerced to 0 — a line saved before FIN-603 has no
    // recorded engine value, and 0 is itself a real VAT rate here (momsfri).
    vatRateAutomatic: row.vat_rate_automatic ?? undefined,
    // undefined (not null) for a line saved before categories were stored: null means
    // "classification failed", undefined means "not classified yet" (FIN-829).
    vatCategory: row.vat_category ?? undefined,
  };
}

/**
 * The shape both saveDraft and updateDraft send: the engine decides a line's VAT unless the
 * user deliberately overrode it (F6), and blank lines are dropped.
 */
function toDraftInput(draft: ReceivableDraft) {
  return {
    customerId: draft.customerId,
    invoiceDate: draft.invoiceDate,
    paymentTermsDays: draft.paymentTermsDays,
    lines: linesToSave(draft.lines).map((l) => ({ ...l, articleId: l.articleId ?? null })),
    sourceReceivableId: draft.sourceInvoiceId ?? null,
    theirReference: draft.theirReference || undefined,
    ourReference: draft.ourReference || undefined,
    // Sent as a real number so a deliberate 0 reaches the backend as 0; `null`
    // is what tells it to render no dröjsmålsränta line at all.
    interestRatePercent: draft.interestRatePercent,
    freeText: draft.freeText || undefined,
    paymentMethod: draft.paymentMethod ?? undefined,
    receivingBankAccountId: draft.receivingBankAccountId,
    documentLanguage: draft.documentLanguage,
    currency: draft.currency,
  };
}

/** A backend receivable row + its lines, as the frontend domain type. */
function mapReceivable(r: ReceivableRowLike, lines: LineItemRowLike[], customerName: string): Receivable {
  return {
    id: r.id,
    number: r.invoice_number ?? "",
    // The real stored status, not a hardcoded "issued" — that is what makes the
    // Draft badge and the Delete / Mark-as-sent actions possible at all.
    status: r.issue_status as IssueStatus,
    customerId: r.customer_id,
    invoiceDate: r.invoice_date,
    paymentTermsDays: r.payment_terms_days ?? 30,
    lines: lines.map(mapLineItem),
    ourReference: r.our_reference ?? "",
    theirReference: r.their_reference ?? "",
    recurrence: "none",
    createdVia: "form",
    ocr: r.payment_reference ?? "",
    issuedAt: r.updated_at,
    customerName,
    toPay: r.gross_total ?? 0,
    // The PDF viewable in the invoice's current state: the rendered draft while it is a
    // draft (FIN-829), the final one once sent.
    pdfPath: r.issue_status === "draft" ? r.draftPdfPath ?? null : r.reissued_pdf_path ?? r.pdf_path,
    version: r.draft_version,
    // `?? null` rather than a falsy check: a stored 0 is a real rate the sender
    // chose, and copying it as "unset" would silently change the new invoice.
    interestRatePercent: r.interest_rate_percent ?? null,
    freeText: r.free_text ?? "",
    paymentMethod: (r.payment_method ?? null) as PaymentMethod | null,
    receivingBankAccountId: r.receiving_bank_account_id ?? null,
    // EC10/AC8: a pre-feature row (no stored value) renders as sv/SEK, never NULL-propagated.
    documentLanguage: (r.document_language ?? "sv") as DocumentLanguage,
    currency: (r.currency ?? "SEK") as InvoiceCurrency,
  };
}

/** The edge function's draft refusals (FIN-829) as the shared domain error; anything else passes through. */
function toDraftEditError(err: unknown): unknown {
  const errorType = (err as InvoiceGenerationApiError | undefined)?.errorType;
  if (errorType === "DRAFT_NO_LONGER_EDITABLE") {
    return new DraftEditError("no_longer_editable");
  }
  if (errorType === "DRAFT_VERSION_CONFLICT") {
    return new DraftEditError("changed_elsewhere");
  }
  if (errorType === "NOT_FOUND") {
    return new DraftEditError("gone");
  }
  return err;
}

export function createRealProvider(getCompanyId: () => Promise<string>): InvoiceGenerationProvider {
  return {
    async getSeller(): Promise<Seller> {
      const companyId = await getCompanyId();
      const { seller } = await invoiceGenerationApiClient.getSellerProfile(companyId);
      return mapSeller(seller as SellerLike);
    },

    async updateSellerProfile(fields): Promise<Seller> {
      const companyId = await getCompanyId();
      const { seller } = await invoiceGenerationApiClient.updateSellerProfile(companyId, fields);
      return mapSeller(seller as SellerLike);
    },

    async createManualBankAccount(input: ManualBankAccountInput): Promise<Seller> {
      const companyId = await getCompanyId();
      await invoiceGenerationApiClient.createManualBankAccount(companyId, {
        clearingNumber: input.clearingNumber,
        accountNumber: input.accountNumber,
        iban: input.iban,
        bankName: input.bankName,
      });
      // Backend returns just the created row — re-fetch so the seller's
      // paymentReferenceLines/manualBankAccounts reflect it immediately.
      const { seller } = await invoiceGenerationApiClient.getSellerProfile(companyId);
      return mapSeller(seller as SellerLike);
    },

    async setBankgiro(bankAccountId: string, typedNumber: string): Promise<Seller> {
      const companyId = await getCompanyId();
      const { seller } = await invoiceGenerationApiClient.setBankAccountBankgiro(companyId, bankAccountId, typedNumber);
      return mapSeller(seller as SellerLike);
    },

    async setCompanyBankgiro(typedNumber: string): Promise<Seller> {
      const companyId = await getCompanyId();
      const { seller } = await invoiceGenerationApiClient.setCompanyBankgiro(companyId, typedNumber);
      return mapSeller(seller as SellerLike);
    },

    async removeCompanyBankgiro(): Promise<Seller> {
      const companyId = await getCompanyId();
      const { seller } = await invoiceGenerationApiClient.removeCompanyBankgiro(companyId);
      return mapSeller(seller as SellerLike);
    },

    async removeBankgiro(bankAccountId: string): Promise<Seller> {
      const companyId = await getCompanyId();
      const { seller } = await invoiceGenerationApiClient.removeBankAccountBankgiro(companyId, bankAccountId);
      return mapSeller(seller as SellerLike);
    },

    async dismissBankgiroNotice(bankAccountId: string): Promise<Seller> {
      const companyId = await getCompanyId();
      const { seller } = await invoiceGenerationApiClient.dismissBankgiroReplacementNotice(companyId, bankAccountId);
      return mapSeller(seller as SellerLike);
    },

    async listCustomers(): Promise<Customer[]> {
      const companyId = await getCompanyId();
      const { customers } = await invoiceGenerationApiClient.listCustomers(companyId);
      return (customers as CustomerRowLike[]).map(mapCustomer);
    },

    async createCustomer(input: NewCustomerInput): Promise<Customer> {
      const companyId = await getCompanyId();
      const { customer } = await invoiceGenerationApiClient.createCustomer(companyId, {
        name: input.name,
        entityType: input.type,
        orgNumber: input.orgNumber,
        vatNumber: input.vatNumber,
        countryCode: input.country,
        email: input.email,
        addressStreet: input.address.street,
        addressPostalCode: input.address.postalCode,
        addressCity: input.address.city,
        addressCountry: input.address.country,
      });
      return mapCustomer(customer as CustomerRowLike);
    },

    async listArticles(): Promise<Article[]> {
      const companyId = await getCompanyId();
      const { articles } = await invoiceGenerationApiClient.suggestArticles(companyId);
      return (articles as ArticleRowLike[]).map(mapArticle);
    },

    async classifyLineVat(description: string): Promise<{ category: string | null; rate: number }> {
      const companyId = await getCompanyId();
      return invoiceGenerationApiClient.classifyLineVat(companyId, description);
    },

    async listReceivables(): Promise<Receivable[]> {
      const companyId = await getCompanyId();
      const [{ receivables }, { customers }] = await Promise.all([
        invoiceGenerationApiClient.listReceivables(companyId),
        invoiceGenerationApiClient.listCustomers(companyId),
      ]);
      const customerNameById = new Map((customers as CustomerRowLike[]).map((c) => [c.id, c.name]));

      // No issue_status filter here any more (FIN-603 D10). Drafts are a real user-facing
      // stage, and which receivables surface at all is decided in ONE place —
      // ReceivableDetailRepository.findAllForCompany — so this app and apps/client cannot
      // drift apart again the way they had (this filter was client-side here, in the BFF there).
      //
      // Line items come batch-attached from listReceivables (one query for every
      // receivable's lines, not one getReceivable call per invoice).
      return (receivables as ReceivableRowLike[]).map((r) =>
        mapReceivable(r, r.lineItems, r.customer_id ? customerNameById.get(r.customer_id) ?? "" : ""),
      );
    },

    async listUploadedInvoices(): Promise<UploadedInvoice[]> {
      const companyId = await getCompanyId();
      const { uploadedReceivables } = await invoiceGenerationApiClient.listUploadedReceivables(companyId);
      return (uploadedReceivables as UploadedInvoiceRowLike[]).map(mapUploadedInvoice);
    },

    async saveDraft(draft: ReceivableDraft, customerName: string): Promise<Receivable> {
      const companyId = await getCompanyId();
      // Create-only: a saved draft is edited through updateDraft, which carries its
      // version and is race-safe against Mark as sent (FIN-829).
      const drafted = await invoiceGenerationApiClient.draftReceivable(companyId, toDraftInput(draft));
      const row = drafted.receivable as ReceivableRowLike;
      return {
        ...mapReceivable({ ...row, draftPdfPath: drafted.pdfPath }, drafted.lineItems as LineItemRowLike[], customerName),
        recurrence: draft.recurrence,
        createdVia: draft.createdVia,
      };
    },

    async getDraftForEdit(receivableId: string): Promise<Receivable> {
      const companyId = await getCompanyId();
      try {
        const [detail, { customers }] = await Promise.all([
          invoiceGenerationApiClient.getReceivable(companyId, receivableId),
          invoiceGenerationApiClient.listCustomers(companyId),
        ]);
        const row = detail.receivable as ReceivableRowLike;
        const name = (customers as CustomerRowLike[]).find((c) => c.id === row.customer_id)?.name ?? "";
        return mapReceivable({ ...row, draftPdfPath: detail.draftPdfPath }, detail.lineItems as LineItemRowLike[], name);
      } catch (err) {
        throw toDraftEditError(err);
      }
    },

    async updateDraft(receivableId: string, expectedVersion: number, draft: ReceivableDraft, customerName: string): Promise<Receivable> {
      const companyId = await getCompanyId();
      try {
        const saved = await invoiceGenerationApiClient.updateReceivableDraft(
          companyId, receivableId, expectedVersion, toDraftInput(draft),
        );
        const row = saved.receivable as ReceivableRowLike;
        return {
          ...mapReceivable({ ...row, draftPdfPath: saved.pdfPath }, saved.lineItems as LineItemRowLike[], customerName),
          recurrence: draft.recurrence,
          createdVia: draft.createdVia,
        };
      } catch (err) {
        throw toDraftEditError(err);
      }
    },

    async ensureDraftPdf(receivableId: string): Promise<string> {
      const companyId = await getCompanyId();
      try {
        const { pdfPath } = await invoiceGenerationApiClient.renderReceivableDraftPdf(companyId, receivableId);
        return pdfPath;
      } catch (err) {
        throw toDraftEditError(err);
      }
    },

    async markAsSent(receivableId: string): Promise<Receivable> {
      const companyId = await getCompanyId();
      const sent = await invoiceGenerationApiClient.issueReceivable(companyId, receivableId);
      const row = sent.receivable as ReceivableRowLike;

      // Re-read rather than patching the caller's copy: mark-as-sent is also the point at
      // which a LOST race returns the winner's row (alreadyIssued), and that row — not
      // this caller's stale draft — is what the user must now see.
      const [{ customers }, detail] = await Promise.all([
        invoiceGenerationApiClient.listCustomers(companyId),
        invoiceGenerationApiClient.getReceivable(companyId, receivableId),
      ]);
      const nameById = new Map((customers as CustomerRowLike[]).map((c) => [c.id, c.name]));
      const full = detail.receivable as ReceivableRowLike;

      return {
        ...mapReceivable(full, detail.lineItems as LineItemRowLike[], full.customer_id ? nameById.get(full.customer_id) ?? "" : ""),
        number: sent.invoiceNumber,
        ocr: sent.ocr,
        issuedAt: row.updated_at,
        toPay: sent.toPay,
        pdfPath: sent.pdfPath,
      };
    },

    async deleteDraft(receivableId: string): Promise<void> {
      const companyId = await getCompanyId();
      await invoiceGenerationApiClient.deleteReceivableDraft(companyId, receivableId);
    },

    async uploadLogo(file: File): Promise<string> {
      const companyId = await getCompanyId();
      const ext = file.name.split(".").pop()?.toLowerCase() || "png";
      // {companyId}/logo.<ext> — the path convention the company-branding bucket's
      // RLS policies key on (storage.foldername(name))[1] = current_company_id()).
      const path = `${companyId}/logo.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("company-branding")
        .upload(path, file, { upsert: true });
      if (uploadError) throw new Error(`Kunde inte ladda upp logotypen: ${uploadError.message}`);

      const { logoUrl } = await invoiceGenerationApiClient.updateLogo(companyId, path);
      if (!logoUrl) throw new Error("Logotypen sparades men kunde inte visas — ladda om sidan.");
      return logoUrl;
    },
  };
}
